import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import SubscriptionModal from './SubscriptionModal';

const SITE_LOGO = "https://customer-assets.emergentagent.com/job_9dd50a71-7bb8-45b9-bdec-566056107363/artifacts/kgdjrixe_Untitled%20design.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home', external: false },
    { path: '/about', label: 'About Us', external: false },
    { path: 'https://events.whatshappeningbaltimore.com', label: 'Events', external: true },
    { path: '/contact', label: 'Contact', external: false },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header 
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass border-b border-white/10' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center">
              <img 
                src={SITE_LOGO} 
                alt="What's Happening Baltimore" 
                className="h-16 md:h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    className="font-medium transition-colors duration-300 text-white hover:text-purple-400 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    className={`font-medium transition-colors duration-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] ${
                      isActive(link.path)
                        ? 'text-purple-400' 
                        : 'text-white hover:text-purple-400'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>

            {/* Subscribe Button (Desktop) */}
            <div className="hidden md:block">
              <button 
                onClick={() => setIsSubscribeModalOpen(true)}
                data-testid="subscribe-btn"
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2.5 rounded-full font-semibold transition-all duration-300"
              >
                Subscribe
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden glass border-t border-white/10"
          >
            <nav className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    className="font-medium py-2 text-zinc-400"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    className={`font-medium py-2 ${
                      isActive(link.path) ? 'text-purple-500' : 'text-zinc-400'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <button 
                onClick={() => {
                  setMobileMenuOpen(false);
                  setIsSubscribeModalOpen(true);
                }}
                className="bg-purple-600 text-white px-6 py-2.5 rounded-full font-semibold text-center mt-2"
              >
                Subscribe
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Subscription Modal */}
      <SubscriptionModal 
        isOpen={isSubscribeModalOpen} 
        onClose={() => setIsSubscribeModalOpen(false)} 
      />
    </>
  );
};

export default Header;
